import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/ui/dialog';
import { Input } from '@/ui/input';
import { Button } from '@/ui/button';
import { Label } from '@/ui/label';
import { Badge } from '@/ui/badge';
import { Link2, AlertCircle, CheckCircle2, Loader2, Link as LinkIcon } from 'lucide-react';
import API from '@/api/api01';
import { toast } from 'sonner';

interface CustomApiModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
}

export default function CustomApiModal({ open, onOpenChange, onSuccess }: CustomApiModalProps) {
  const [url, setUrl] = useState('');
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);
  const [isValidating, setIsValidating] = useState(false);
  const [validationStatus, setValidationStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const resetForm = () => {
    setUrl('');
    setName('');
    setLoading(false);
    setIsValidating(false);
    setValidationStatus('idle');
  };

  const handleOpenChange = (isOpen: boolean) => {
    if (!isOpen) {
      resetForm();
    }
    onOpenChange(isOpen);
  };

  const handleValidate = async () => {
    if (!url.trim()) {
      toast.error('Please enter an API URL first');
      return;
    }
    
    // Basic formatting check
    if (!url.startsWith('http://') && !url.startsWith('https://')) {
      toast.error('URL must start with http:// or https://');
      return;
    }

    try {
      setIsValidating(true);
      setValidationStatus('idle');
      const res = await API.post('/superadmin/custom-api/validate', { url });
      
      if (res.data.valid) {
        setValidationStatus('success');
        toast.success('API Validated Successfully');
        if (res.data.name && !name) {
          setName(res.data.name);
        }
      } else {
        setValidationStatus('error');
        toast.error('Could not validate API: ' + (res.data.error || 'Unknown error'));
      }
    } catch (err: any) {
      console.error('Validation error', err);
      setValidationStatus('error');
      toast.error('Failed to validate the API endpoint.');
    } finally {
      setIsValidating(false);
    }
  };

  const handleSave = async () => {
    if (!url.trim() || !name.trim()) {
      toast.error('Please fill in both URL and Name fields');
      return;
    }

    try {
      setLoading(true);
      await API.post('/superadmin/custom-api', {
        url,
        name,
        status: 'Active'
      });
      toast.success('Custom API Integration added successfully');
      onSuccess();
      handleOpenChange(false);
    } catch (err: any) {
      console.error(err);
      toast.error(err.response?.data?.detail || 'Failed to save integration');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[480px] rounded-[2rem] border-none shadow-2xl p-0 overflow-hidden">
        
        {/* Header */}
        <div className="bg-slate-900 border-b border-slate-800 p-8 text-white relative">
          <div className="absolute top-0 left-0 w-full h-full bg-[radial-gradient(circle_at_top_right,rgba(168,85,247,0.1),transparent)]" />
          <div className="relative z-10">
            <div className="flex items-center gap-4 mb-3">
              <div className="h-12 w-12 rounded-xl bg-slate-800/50 flex items-center justify-center border border-slate-700/50 backdrop-blur-sm">
                <LinkIcon size={24} className="text-purple-400" />
              </div>
              <div>
                <DialogTitle className="text-2xl font-black tracking-tight text-white mb-1">
                  Custom API Add
                </DialogTitle>
                <DialogDescription className="text-slate-400 font-medium text-sm m-0">
                  Connect a dynamic endpoint.
                </DialogDescription>
              </div>
            </div>
          </div>
        </div>

        {/* Body */}
        <div className="p-8 space-y-6 bg-card text-foreground">
          
          <div className="space-y-2">
            <Label className="text-xs font-black uppercase tracking-widest text-slate-500">API Endpoint URL</Label>
            <div className="flex gap-2">
              <div className="relative flex-1">
                <div className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">
                  <Link2 size={16} />
                </div>
                <Input 
                  placeholder="https://api.example.com/data" 
                  value={url}
                  onChange={(e) => {
                    setUrl(e.target.value);
                    if (validationStatus !== 'idle') setValidationStatus('idle');
                  }}
                  className="pl-9 bg-slate-50 border-slate-200 h-12 rounded-xl focus-visible:ring-purple-500"
                />
              </div>
              <Button 
                variant="secondary" 
                onClick={handleValidate} 
                disabled={isValidating || !url.trim()}
                className="h-12 px-6 rounded-xl font-bold bg-slate-100 hover:bg-slate-200 text-slate-700"
              >
                {isValidating ? <Loader2 size={18} className="animate-spin" /> : 'Detect'}
              </Button>
            </div>
            
            {/* Status indicator */}
            {validationStatus === 'success' && (
              <div className="flex items-center gap-1.5 text-emerald-600 text-[11px] font-bold mt-2">
                <CheckCircle2 size={14} /> Valid Endpoint Detected
              </div>
            )}
            {validationStatus === 'error' && (
              <div className="flex items-center gap-1.5 text-red-500 text-[11px] font-bold mt-2">
                <AlertCircle size={14} /> Connection Failed or Blocked
              </div>
            )}
          </div>

          <div className="space-y-2">
            <Label className="text-xs font-black uppercase tracking-widest text-slate-500">Integration Name</Label>
            <Input 
              placeholder="e.g. My Backend Source" 
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="bg-slate-50 border-slate-200 h-12 rounded-xl focus-visible:ring-purple-500 font-medium"
            />
            <p className="text-[10px] text-slate-400 font-medium">
              If detection fails, type a recognizable name manually here.
            </p>
          </div>

        </div>

        {/* Footer */}
        <DialogFooter className="p-6 pt-0 sm:justify-between border-t border-slate-100">
          <Button variant="ghost" onClick={() => handleOpenChange(false)} className="rounded-xl font-bold text-slate-500 hover:text-slate-700">
            Cancel
          </Button>
          <Button 
            onClick={handleSave} 
            disabled={loading || !url.trim() || !name.trim()}
            className="rounded-xl px-8 bg-purple-600 hover:bg-purple-700 font-black shadow-lg shadow-purple-200"
          >
            {loading ? <Loader2 size={16} className="animate-spin mr-2" /> : null}
            Save Integration
          </Button>
        </DialogFooter>

      </DialogContent>
    </Dialog>
  );
}
